import { Button, ColumnTable, Icon } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col } from 'reactstrap';
import { PathsLayouts } from '../../../config/routes';
import { TranslationsKeys } from '../../../locales/translations';
import BadgeStatus from '../parts/BadgeStatus';
import { useHistory } from 'react-router-dom';
import { ClassesBookActivitiesStatus, ClassesBookItem } from '../types';

type UseClassesBookColumnsProps = {
  onDownloadReport?: (url: string) => void;
};

export const useClassesBookColumns = ({
  onDownloadReport,
}: UseClassesBookColumnsProps) => {
  const { t } = useTranslation(TranslationsKeys.CLASSES_BOOK_LIST);
  const history = useHistory();

  const columns = useMemo<ColumnTable<ClassesBookItem<any>>[]>(
    () => [
      {
        headerText: t(`table.period`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'period',
        cellFormat(options) {
          const isContinuousEducation =
            options.row.section?.isContinuousEducation;
          return isContinuousEducation ? 'N/A' : options.value?.name ?? 'N/A';
        },
      },
      {
        headerText: t(`table.section`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'section',
        cellFormat({ value }) {
          return value?.name;
        },
      },
      {
        headerText: t(`table.courseCode`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'course',
        cellFormat({ value }) {
          return value?.code;
        },
      },
      {
        headerText: t(`table.courseName`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'course',
        cellFormat({ value }) {
          return value?.name;
        },
      },
      {
        headerText: t(`table.school`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'school',
        cellFormat({ value }) {
          return value?.name;
        },
      },
      {
        headerText: t(`table.campus`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'campus',
        cellFormat({ value }) {
          return value?.name;
        },
      },
      {
        // HU 15447 — Task B16 (D1): "Unidades de aprendizaje" va ANTES de
        // "Programa de actividad" para alinearse con el mockup XD.
        // HU 15447 — Task C19: el docente ve en solo lectura si la asignatura
        // tiene unidades de aprendizaje cargadas (flag hasLearningUnits viene
        // del backend en C18). Sin acción de edición: la carga se realiza
        // desde el portal de administración.
        headerText: t(`table.learningUnits`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'hasLearningUnits',
        cellFormat({ row }) {
          return (
            <BadgeStatus
              status={
                row?.hasLearningUnits
                  ? ClassesBookActivitiesStatus.Loaded
                  : ClassesBookActivitiesStatus.NonLoaded
              }
            />
          );
        },
      },
      {
        headerText: t(`table.courseProgram`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'status',
        cellFormat({ row }) {
          const hasSyllabus = !!row?.syllabusUrl?.trim();
          return (
            <Col className="d-flex align-items-center justify-content-center">
              <button
                className="btn"
                disabled={!hasSyllabus}
                onClick={() => {
                  onDownloadReport &&
                    row?.syllabusUrl?.trim() &&
                    onDownloadReport(row.syllabusUrl);
                }}
              >
                <Icon
                  name="download"
                  color={hasSyllabus ? 'primary' : 'disabled'}
                />
              </button>
            </Col>
          );
        },
      },
      {
        headerText: t(`table.action`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        columnName: 'section',
        cellFormat({ value }) {
          return (
            <Col className="mb-2">
              <Button
                text={t('table.btn.edit')}
                size="sm"
                fullwidth
                onClick={() =>
                  history.push(`${PathsLayouts.classesBook}/${value.id}`)
                }
              />
            </Col>
          );
        },
      },
    ],
    [t, onDownloadReport, history],
  );
  return columns;
};
